import MusicXML from './MusicXML';
import {
  MidiEventState,
  MidiEventType,
  MidiFullErrorStates,
  PrerenderedPhraseData,
  PrerenderedVoiceEntry,
  errorScores,
} from 'Types';
import { EventBucket, NoteEvent, RestEvent } from 'Models/EventStream';
import { sum, cloneDeep, rest } from 'lodash';
import { PhraseNoteError } from 'Types/EventTypes';
import {
  midiFullErrorStates,
  midiSemiErrorStates,
  midiAllErrorStates,
} from 'Types';
import logger from 'Utils/Logger';
import TimeSignature from './TimeSignature';
import { HANDEDNESS } from 'Utils/Constants';
import { isErrorState, isSemiErrorState } from 'Utils';
import { MeasureRect } from 'Phaser/GameObjects/StaffLine';

const END_PHRASE_LEFT = 'END_PHRASE_LEFT',
  END_PHRASE_WIDTH = 'END_PHRASE_WIDTH';

export enum AccuracyTypes {
  avg_running_accuracy = 'avg_running_accuracy',
  avg_lh_running_accuracy = 'avg_lh_running_accuracy',
  avg_rh_running_accuracy = 'avg_rh_running_accuracy',
  avg_accuracy = 'avg_accuracy',
  avg_lh_accuracy = 'avg_lh_accuracy',
  avg_rh_accuracy = 'avg_rh_accuracy',
}

export type AccuracyType =
  | AccuracyTypes.avg_accuracy
  | AccuracyTypes.avg_lh_accuracy
  | AccuracyTypes.avg_rh_accuracy
  | AccuracyTypes.avg_running_accuracy
  | AccuracyTypes.avg_lh_running_accuracy
  | AccuracyTypes.avg_rh_running_accuracy;

export const UNPLAYED_NOTE_TICK = -1;

export class Iterator {
  cursorData: PrerenderedPhraseData[];
  phrase: Phrase;
  indx: number = 0;
  currentMeasureIndex: number = 0;
  protected endReached: boolean = false;
  currentTimeStamp: number = 0;
  noteLocationCache: Map<string, number> = new Map();
  svg: HTMLElement | null = null;

  constructor(cursorData: PrerenderedPhraseData[], phrase: Phrase) {
    this.cursorData = cursorData;
    this.phrase = phrase;
    if (cursorData.length) {
      // logger.info(phraseUuid)
      // logger.info(cursorData)
      this.currentMeasureIndex = cursorData[0].currentMeasureIndex;
      this.currentTimeStamp = cursorData[0].currentTimestamp;
    } else {
      this.endReached = true;
    }
  }

  next() {
    this.indx += 1;
    if (this.indx >= this.cursorData.length) {
      this.endReached = true;
    } else {
      this.currentMeasureIndex = this.cursorData[this.indx].currentMeasureIndex;
      this.currentTimeStamp = this.cursorData[this.indx].currentTimestamp;
    }
  }

  public get EndReached(): boolean {
    return this.endReached;
  }

  public get CurrentMeasureIndex(): number {
    return this.currentMeasureIndex;
  }

  public get CurrentVoiceEntries(): PrerenderedVoiceEntry[] {
    return this.cursorData[this.indx].currentVoiceEntries;
  }

  // Function is extracted out as it needs to be slightly modified
  // in derived classes to be compatible with phaser
  getGraphicsLeft(currentNoteId: string): number | undefined {
    const element = document.getElementById(currentNoteId);
    const left = element?.getBoundingClientRect()?.left;
    if (left) {
      this.noteLocationCache.set(currentNoteId, left);
    }
    return left;
  }

  // Function is extracted out as it needs to be slightly modified
  // in derived classes to be compatible with phaser
  getPhraseLeft(): number | undefined {
    if (this.phrase.uuid) {
      // prevent unnecessary DOM query if using cached locations
      let left = this.noteLocationCache.get(END_PHRASE_LEFT);
      let width = this.noteLocationCache.get(END_PHRASE_WIDTH);
      if (left && width) {
        return left + width;
      }
      if (!this.svg) {
        this.svg = document.getElementById(this.phrase.uuid);
      }
      if (this.svg && (!left || !width)) {
        left = this.svg.getBoundingClientRect().left;
        this.noteLocationCache.set(END_PHRASE_LEFT, left);
        width = this.svg.getBoundingClientRect().width;
        this.noteLocationCache.set(END_PHRASE_WIDTH, width);
      }
      if (left && width) {
        return left + width;
      } else {
        console.error('LEFT OR WIDTH NOT FOUND');
        logger.info('left: ' + left);
        logger.info('width: ' + width);
      }

      if (!this.svg) {
        console.warn('no SVG found for phrase id: ' + this.phrase.uuid);
      }
    } else {
      console.warn(
        'no phrase id defined in Phrase iterator - this should only happen if phrases are rendered in browser'
      );
    }
  }

  public left(): number | undefined {
    // returns the equivalent "left" value of the current cursor iterator location in the
    // phrase SVG

    if (this.EndReached) {
      return this.getPhraseLeft();
    } else {
      // best option here is to try to find the first non rest note because whole note long rests will be in
      // the middle of the measure, which throws off animation.
      let noteIndx = 0,
        voiceEntryIndex = 0;
      let note = this.cursorData[this.indx]?.currentVoiceEntries[0]?.Notes[0];
      let shortestNoteLength: number | undefined = undefined;
      while (
        voiceEntryIndex < this.cursorData[this.indx].currentVoiceEntries.length
      ) {
        while (
          noteIndx <
          this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex].Notes
            .length
        ) {
          const newNote =
            this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex]
              .Notes[noteIndx];
          if (
            shortestNoteLength === undefined ||
            newNote.length <= shortestNoteLength
          ) {
            // typically rests will be later in the measure even if they are the same length (i.e. dotted half note vs 3/4 whole note rest)
            if (
              newNote.isRest &&
              !note.isRest &&
              newNote.length == shortestNoteLength
            ) {
              break;
            } else {
              note =
                this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex]
                  .Notes[noteIndx];
              shortestNoteLength = note.length;
            }
          }
          noteIndx += 1;
        }
        noteIndx = 0;
        voiceEntryIndex += 1;
      }
      const currentNoteId: string = note?.graphics[0]?.staveNote?.id;
      if (currentNoteId) {
        let left = this.noteLocationCache.get(currentNoteId);
        if (!left) {
          left = this.getGraphicsLeft(currentNoteId);
        }
        return left;
      }
      return 0;
    }
  }

  // The phaser scenes calculate the locations of every interactive element
  // (Notes / Rests) at the time of rendering the phrases so we can just
  // use those precalculated values without having the recompute them
  // during playback
  setLocationCache(locations: Map<string, number>) {
    this.noteLocationCache = locations;
  }

  setPhraseEnd(left: number, width: number) {
    this.noteLocationCache.set(END_PHRASE_LEFT, left);
    this.noteLocationCache.set(END_PHRASE_WIDTH, width);
  }

  public clearCache() {
    this.noteLocationCache = new Map<string, number>();
  }

  public resetIterator() {
    this.indx = 0;
    if (this.cursorData.length) {
      this.currentMeasureIndex = this.cursorData[0].currentMeasureIndex;
      this.currentTimeStamp = this.cursorData[0].currentTimestamp;
      this.endReached = false;
    } else {
      this.endReached = true;
    }
  }

  peekNext(): PrerenderedPhraseData | undefined {
    if (this.indx >= this.cursorData.length - 1) return undefined;
    else {
      return this.cursorData[this.indx + 1];
    }
  }
}

// TEMPORARY. The phaser iterator should take a phrase just as the iterator does
export class IteratorPhaser {
  cursorData: PrerenderedPhraseData[];
  phraseUuid: string;
  phrase: undefined = undefined;
  indx: number = 0;
  currentMeasureIndex: number = 0;
  protected endReached: boolean = false;
  currentTimeStamp: number = 0;
  noteLocationCache: Map<string, number> = new Map();
  svg: HTMLElement | null = null;

  constructor(cursorData: PrerenderedPhraseData[], phraseUuid: string) {
    this.cursorData = cursorData;
    this.phraseUuid = phraseUuid;
    if (cursorData.length) {
      // logger.info(phraseUuid)
      // logger.info(cursorData)
      this.currentMeasureIndex = cursorData[0].currentMeasureIndex;
      this.currentTimeStamp = cursorData[0].currentTimestamp;
    } else {
      this.endReached = true;
    }
  }

  next() {
    this.indx += 1;
    if (this.indx >= this.cursorData.length) {
      this.endReached = true;
    } else {
      this.currentMeasureIndex = this.cursorData[this.indx].currentMeasureIndex;
      this.currentTimeStamp = this.cursorData[this.indx].currentTimestamp;
    }
  }

  public get EndReached(): boolean {
    return this.endReached;
  }

  public get CurrentMeasureIndex(): number {
    return this.currentMeasureIndex;
  }

  public get CurrentVoiceEntries(): PrerenderedVoiceEntry[] {
    return this.cursorData[this.indx].currentVoiceEntries;
  }

  // Function is extracted out as it needs to be slightly modified
  // in derived classes to be compatible with phaser
  getGraphicsLeft(currentNoteId: string): number | undefined {
    const element = document.getElementById(currentNoteId);
    const left = element?.getBoundingClientRect()?.left;
    if (left) {
      this.noteLocationCache.set(currentNoteId, left);
    }
    return left;
  }

  // Function is extracted out as it needs to be slightly modified
  // in derived classes to be compatible with phaser
  getPhraseLeft(): number | undefined {
    if (this.phraseUuid) {
      // prevent unnecessary DOM query if using cached locations
      let left = this.noteLocationCache.get(END_PHRASE_LEFT);
      let width = this.noteLocationCache.get(END_PHRASE_WIDTH);
      if (left && width) {
        return left + width;
      }
      if (!this.svg) {
        this.svg = document.getElementById(this.phraseUuid);
      }
      if (this.svg && (!left || !width)) {
        left = this.svg.getBoundingClientRect().left;
        this.noteLocationCache.set(END_PHRASE_LEFT, left);
        width = this.svg.getBoundingClientRect().width;
        this.noteLocationCache.set(END_PHRASE_WIDTH, width);
      }
      if (left && width) {
        return left + width;
      } else {
        console.error('LEFT OR WIDTH NOT FOUND');
        logger.info('left: ' + left);
        logger.info('width: ' + width);
      }

      if (!this.svg) {
        console.warn('no SVG found for phrase id: ' + this.phraseUuid);
      }
    } else {
      console.warn(
        'no phrase id defined in Phrase iterator - this should only happen if phrases are rendered in browser'
      );
    }
  }

  public left(): number | undefined {
    // returns the equivalent "left" value of the current cursor iterator location in the
    // phrase SVG

    if (this.EndReached) {
      return this.getPhraseLeft();
    } else {
      // best option here is to try to find the first non rest note because whole note long rests will be in
      // the middle of the measure, which throws off animation.
      let noteIndx = 0,
        voiceEntryIndex = 0;
      let note = this.cursorData[this.indx].currentVoiceEntries[0].Notes[0];
      let shortestNoteLength: number | undefined = undefined;
      while (
        voiceEntryIndex < this.cursorData[this.indx].currentVoiceEntries.length
      ) {
        while (
          noteIndx <
          this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex].Notes
            .length
        ) {
          const newNote =
            this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex]
              .Notes[noteIndx];
          if (
            shortestNoteLength === undefined ||
            newNote.length <= shortestNoteLength
          ) {
            // typically rests will be later in the measure even if they are the same length (i.e. dotted half note vs 3/4 whole note rest)
            if (
              newNote.isRest &&
              !note.isRest &&
              newNote.length == shortestNoteLength
            ) {
              break;
            } else {
              note =
                this.cursorData[this.indx].currentVoiceEntries[voiceEntryIndex]
                  .Notes[noteIndx];
              shortestNoteLength = note.length;
            }
          }
          noteIndx += 1;
        }
        noteIndx = 0;
        voiceEntryIndex += 1;
      }
      const currentNoteId: string = note?.graphics[0]?.staveNote?.id;
      if (currentNoteId) {
        let left = this.noteLocationCache.get(currentNoteId);
        if (!left) {
          left = this.getGraphicsLeft(currentNoteId);
        }
        return left;
      }
      return 0;
    }
  }

  // The phaser scenes calculate the locations of every interactive element
  // (Notes / Rests) at the time of rendering the phrases so we can just
  // use those precalculated values without having the recompute them
  // during playback
  setLocationCache(locations: Map<string, number>) {
    this.noteLocationCache = locations;
  }

  setPhraseEnd(left: number, width: number) {
    this.noteLocationCache.set(END_PHRASE_LEFT, left);
    this.noteLocationCache.set(END_PHRASE_WIDTH, width);
  }

  public clearCache() {
    this.noteLocationCache = new Map<string, number>();
  }

  public resetIterator() {
    this.indx = 0;
    if (this.cursorData.length) {
      this.currentMeasureIndex = this.cursorData[0].currentMeasureIndex;
      this.currentTimeStamp = this.cursorData[0].currentTimestamp;
      this.endReached = false;
    } else {
      this.endReached = true;
    }
  }

  peekNext(): PrerenderedPhraseData | undefined {
    if (this.indx >= this.cursorData.length - 1) return undefined;
    else {
      return this.cursorData[this.indx + 1];
    }
  }
}

export type MidiEvent = {
  midiEvent: MidiEventType;
  midiNote: number;
  timestamp: number;
};

class Phrase {
  readonly musicXML: MusicXML;
  protected svgURL: string;
  protected svgTimesigURL: string;
  protected cursorData: PrerenderedPhraseData[];
  protected cursorDataTimesig: PrerenderedPhraseData[];
  protected iterator: Iterator;
  protected timesigIterator: Iterator;
  readonly uuid: string;
  protected startTimestamp: number = 0;
  protected endTimestamp: number = 0;
  protected pickUpMeasureLength: number = 0;
  protected pickUpMeasureOffset: number = 0;
  protected timeSigFract: number = 0;
  protected midiEvents: {
    midiEvent: MidiEventType;
    midiNote: number;
    timestamp: number;
  }[] = [];
  protected playData: EventBucket[] = [];
  protected runningAccuracy: number[] = [];
  protected lhRunningAccuracy: number[] = [];
  protected rhRunningAccuracy: number[] = [];
  protected complete: boolean = false;
  protected hasLeftHandNotes = false;
  protected hasRightHandNotes = false;
  protected numNotes;
  protected measureWidths: MeasureRect[];

  // This will at some point contain all tags
  constructor(
    uuid: string,
    musicXML: MusicXML,
    numNotes: number,
    svgURL: string,
    svgTimesigURL: string,
    cursorData: PrerenderedPhraseData[],
    cursorDataTimesig: PrerenderedPhraseData[],
    measureWidths: MeasureRect[]
  ) {
    this.uuid = uuid;
    this.musicXML = musicXML;
    this.svgURL = svgURL;
    this.svgTimesigURL = svgTimesigURL;
    this.cursorData = cursorData;
    this.cursorDataTimesig = cursorDataTimesig;
    this.numNotes = numNotes;
    this.iterator = new Iterator(cursorData, this);
    this.measureWidths = measureWidths;
    this.timesigIterator = new Iterator(cursorDataTimesig, this);
    this.timeSigFract =
      this.musicXML.timeSignatures[0].numerator /
      this.musicXML.timeSignatures[0]?.denominator;
    this.pickUpMeasureLength = this.calcPickUpLength();
    // I feel like this could be more simple but don't have the time
    this.pickUpMeasureOffset =
      this.pickUpMeasureLength == 0
        ? 0
        : this.timeSigFract - this.pickUpMeasureLength;
    this.hasLeftHandNotes =
      cursorData.reduce((prev, curr) => {
        return (
          prev +
          curr.currentVoiceEntries.reduce((acc, currVoiceEntry) => {
            return (
              acc +
              currVoiceEntry.Notes.reduce(
                (acc, note) => acc + (note.staffPosition === 'bottom' ? 1 : 0),
                0
              )
            );
          }, 0)
        );
      }, 0) > 0;
    this.hasRightHandNotes =
      cursorData.reduce((prev, curr) => {
        return (
          prev +
          curr.currentVoiceEntries.reduce((acc, currVoiceEntry) => {
            return (
              acc +
              currVoiceEntry.Notes.reduce(
                (acc, note) => acc + (note.staffPosition === 'top' ? 1 : 0),
                0
              )
            );
          }, 0)
        );
      }, 0) > 0;
    // this.calcPickUpLength()
  }

  private calcPickUpLength() {
    const timeSigNum = this.musicXML.timeSignatures[0]?.numerator;
    const timeSigDenom = this.musicXML.timeSignatures[0]?.denominator;
    const timeSigFract = timeSigNum / timeSigDenom;
    if (this.cursorData) {
      const lastDatum = this.cursorData[this.cursorData?.length - 1];
      const voiceEntries = lastDatum.currentVoiceEntries;
      const randomVoiceEntry = voiceEntries[voiceEntries.length - 1];
      const randomNote =
        randomVoiceEntry.Notes[randomVoiceEntry.Notes.length - 1];
      const randomLastVoiceEntryNoteEndTimestamp =
        lastDatum.currentTimestamp + randomNote.length;
      return randomLastVoiceEntryNoteEndTimestamp % timeSigFract;
    }
    return this.pickUpMeasureLength;
  }

  private calcEndTimestamp() {
    const lastDatum = this.cursorData[this.cursorData?.length - 1];
    const voiceEntries = lastDatum.currentVoiceEntries;
    const randomVoiceEntry = voiceEntries[voiceEntries.length - 1];
    const randomNote =
      randomVoiceEntry.Notes[randomVoiceEntry.Notes.length - 1];
    const randomLastVoiceEntryNoteEndTimestamp =
      lastDatum.currentTimestamp + randomNote.length;
    return randomLastVoiceEntryNoteEndTimestamp;
  }

  public setStartTimestamp(startTimestamp: number) {
    this.startTimestamp = startTimestamp;
    this.endTimestamp = this.calcEndTimestamp() + startTimestamp;
  }

  public getStartTimestamp(): number {
    return this.startTimestamp;
  }

  public getEndTimestamp(): number {
    return this.endTimestamp;
  }

  public getTimeSigFract(): number {
    return this.timeSigFract;
  }

  public getPickUpMeasureOffset(): number {
    return this.pickUpMeasureOffset;
  }

  public getIterator() {
    return this.iterator;
  }

  public getTimesigIterator() {
    return this.timesigIterator;
  }

  public getHasRightHandedNotes() {
    return this.hasRightHandNotes;
  }

  public getHasLeftHandedNotes() {
    return this.hasLeftHandNotes;
  }

  public pushEvent(midiEvent: MidiEvent) {
    this.midiEvents.push(midiEvent);
  }

  public pushRunningAccuracy(accuracy: number) {
    this.runningAccuracy.push(accuracy);
  }

  public pushLHRunningAccuracy(accuracy: number) {
    this.lhRunningAccuracy.push(accuracy);
  }

  public pushRHRunningAccuracy(accuracy: number) {
    this.rhRunningAccuracy.push(accuracy);
  }

  public pushPhrasePlayData(bucket: EventBucket) {
    this.playData.push(bucket);
  }

  public getSvgUrl() {
    return this.svgURL;
  }

  public getSvgTimesigUrl() {
    return this.svgTimesigURL;
  }

  public getComplete() {
    return this.complete;
  }

  public getMeasureData() {
    return {
      serializedIterator: this.cursorData,
      measureRects: this.measureWidths,
    };
  }

  public setComplete(complete: boolean) {
    this.complete = complete;
  }

  /**
   * Resets all play-related data for replay functionality.
   * This clears playback state while preserving the phrase's music content.
   */
  public resetPlayData() {
    this.midiEvents = [];
    this.playData = [];
    this.runningAccuracy = [];
    this.lhRunningAccuracy = [];
    this.rhRunningAccuracy = [];
    this.complete = false;
    this.startTimestamp = 0;
    this.endTimestamp = 0;
    // Reset the iterators so events can be re-added
    this.iterator.resetIterator();
    this.timesigIterator.resetIterator();
  }

  private calcRunningAccuracy(hand?: HANDEDNESS): number {
    // return this.cursorData.reduce((accum: number, datum) =>{
    //   return datum.currentVoiceEntries.reduce((accum: number, currentVoiceEntry) =>{
    //     return currentVoiceEntry.Notes.filter(note => note.staffPosition === 'bottom').map(note => note.)
    //   },0) + accum;
    // }, 0)
    let totalNoteAndRestCount = 0;
    let totalAcc = 0;
    const totalAccuracy = this.playData.reduce(
      (acc: number, datum: EventBucket) => {
        const noteAcc = datum.notes.reduce((acc: number, note: NoteEvent) => {
          if (hand && hand === HANDEDNESS.LEFT) {
            totalNoteAndRestCount += 1;
            return note.Hand === hand && note
              ? note.LhSnapshotPhraseAccuracy + acc
              : acc;
          }
          if (hand && hand === HANDEDNESS.RIGHT) {
            totalNoteAndRestCount += 1;
            return note.Hand === hand
              ? note.RhSnapshotPhraseAccuracy + acc
              : acc;
          }
          totalNoteAndRestCount += 1;
          return note.SnapshotPhraseAccuracy + acc;
        }, 0);

        const restAcc = datum.rests.reduce((acc: number, rest: RestEvent) => {
          const restSnapshotAccuracy = rest.SnapshotPhraseAccuracy;
          if (restSnapshotAccuracy !== undefined) {
            if (hand && hand === HANDEDNESS.LEFT) {
              let LHSnapshotAccuracy = rest.LhSnapshotPhraseAccuracy;
              if (LHSnapshotAccuracy) {
                totalNoteAndRestCount += 1;
                return rest.Hand === hand ? LHSnapshotAccuracy + acc : acc;
              } else {
                totalNoteAndRestCount += 1;
                return acc;
              }
            } else if (hand && hand === HANDEDNESS.RIGHT) {
              let rhSnapshotAccuracy = rest.LhSnapshotPhraseAccuracy;
              if (rhSnapshotAccuracy) {
                totalNoteAndRestCount += 1;
                return rest.Hand === hand ? rhSnapshotAccuracy + acc : acc;
              } else {
                totalNoteAndRestCount += 1;
                return acc;
              }
            }
            totalNoteAndRestCount += 1;
            return acc + restSnapshotAccuracy;
          }
          return acc;
        }, 0);

        return restAcc + noteAcc + acc;
      },
      0
    );

    const totalNoteAndRestSum = this.playData.reduce(
      (acc: number, datum: EventBucket) => {
        const noteSum = datum.notes.reduce((acc: number, note: NoteEvent) => {
          if (hand) {
            return note.Hand === hand ? 1 + acc : acc;
          } else {
            return 1 + acc;
          }
        }, 0);

        const restSum = datum.rests.reduce((acc: number, rest: RestEvent) => {
          const restSnapshotAccuracy = rest.SnapshotPhraseAccuracy;
          if (restSnapshotAccuracy !== undefined) {
            if (hand) {
              return rest.Hand === hand ? 1 + acc : acc;
            }
            return 1 + acc;
          }
          return acc;
        }, 0);
        return noteSum + restSum + acc;
      },
      0
    );
    if (totalNoteAndRestSum > 0) {
      return totalAccuracy / totalNoteAndRestSum;
    } else {
      return -1;
    }
  }

  private calcAccuracy2(hand?: HANDEDNESS): number {
    const getNoteAccuracyfromEndState = (midiEventState: MidiEventState) => {
      if (isSemiErrorState(midiEventState)) {
        return 0.7;
      } else if (isErrorState(midiEventState)) {
        return 0;
      } else {
        return 1;
      }
    };
    const totalAccuracy = this.playData.reduce(
      (acc: number, datum: EventBucket) => {
        const noteAcc = datum.notes.reduce((acc: number, note: NoteEvent) => {
          if (hand) {
            return note.Hand === hand
              ? getNoteAccuracyfromEndState(note.OffsetState) + acc
              : acc;
          }
          return getNoteAccuracyfromEndState(note.OffsetState) + acc;
        }, 0);

        const restAcc = datum.rests.reduce((acc: number, rest: RestEvent) => {
          const restSnapshotAccuracy = rest.SnapshotPhraseAccuracy;
          if (restSnapshotAccuracy !== undefined) {
            if (hand) {
              //TODO: make not magic number
              return rest.Hand === hand ? 0.7 + acc : acc;
            }
            return 0.7 + acc;
          }
          return acc;
        }, 0);

        return restAcc + noteAcc + acc;
      },
      0
    );

    const totalNoteAndRestSum = this.playData.reduce(
      (acc: number, datum: EventBucket) => {
        const noteSum = datum.notes.reduce((acc: number, note: NoteEvent) => {
          if (hand) {
            return note.Hand === hand ? 1 + acc : acc;
          } else {
            return 1 + acc;
          }
        }, 0);

        const restSum = datum.rests.reduce((acc: number, rest: RestEvent) => {
          const restSnapshotAccuracy = rest.SnapshotPhraseAccuracy;
          // it's kind of implied here that the handedsnapshot accuracy would be available
          if (restSnapshotAccuracy !== undefined) {
            if (hand) {
              return rest.Hand === hand ? 1 + acc : acc;
            }
            return 1 + acc;
          }
          return acc;
        }, 0);
        return noteSum + restSum + acc;
      },
      0
    );
    logger.info('totalAccuracy (accuracy) for ' + hand, totalAccuracy);
    logger.info('totalNoteAndRestSum (accuracy)', totalNoteAndRestSum);

    if (totalNoteAndRestSum > 0) {
      return (totalAccuracy / totalNoteAndRestSum) * 100;
    } else {
      return -1;
    }
  }

  private getAccuracy(
    accuracyType:
      | AccuracyTypes.avg_lh_accuracy
      | AccuracyTypes.avg_rh_accuracy
      | AccuracyTypes.avg_accuracy
  ): number {
    logger.info('running avg accuracy. playdata: ', this.playData);
    if (accuracyType === AccuracyTypes.avg_lh_accuracy) {
      logger.info(
        'getting avg lh running accuracy.',
        this.calcAccuracy2(HANDEDNESS.LEFT)
      );
      // logger.info("this.lhRunningAccuracy arr: ", this.lhRunningAccuracy)
      // logger.info("calculated lh running accuracy: ",  (sum(this.lhRunningAccuracy) / (this.lhRunningAccuracy.length * 100)) * 100)
      // return this.lhRunningAccuracy.length === 0 ? null :  (sum(this.lhRunningAccuracy) / (this.lhRunningAccuracy.length * 100)) * 100
      return this.calcAccuracy2(HANDEDNESS.LEFT);
    } else if (accuracyType === AccuracyTypes.avg_rh_accuracy) {
      logger.info(
        'getting avg rh running accuracy.',
        this.calcAccuracy2(HANDEDNESS.RIGHT)
      );
      // logger.info("this.rhRunningAccuracy arr: ", this.rhRunningAccuracy)
      // logger.info("calculated rh running accuracy: ", (sum(this.rhRunningAccuracy) / (this.rhRunningAccuracy.length * 100)) * 100)
      // return this.rhRunningAccuracy.length === 0 ? null : (sum(this.rhRunningAccuracy) / (this.rhRunningAccuracy.length * 100)) * 100
      return this.calcAccuracy2(HANDEDNESS.RIGHT);
    }
    logger.info('getting avg running accuracy.', this.calcAccuracy2());
    // logger.info("this.runningAccuracyy arr: ", this.runningAccuracy)
    // logger.info("calculated running accuracy: ", (sum(this.runningAccuracy) / (this.runningAccuracy.length * 100)) * 100)
    // return this.runningAccuracy.length === 0 ? null : (sum(this.runningAccuracy) / (this.runningAccuracy.length * 100)) * 100
    return this.calcAccuracy2();
  }

  private calcAccuracy(accuracyType: AccuracyType) {
    logger.debug('getting accuracy. Accuracy type: ', accuracyType);
    const playData = this.filterPlayDataByAccuracyType(accuracyType);
    const totalNotes = playData.reduce((prevBucket, currBucket) => {
      return prevBucket + currBucket.notes.length;
    }, 0);
    const totalRests = playData.reduce((prevBucket, currBucket) => {
      return (
        prevBucket + currBucket.rests.filter((rest) => !rest.Hidden).length
      );
    }, 0);
    const totalSemiCorrect = playData.reduce(
      (prevBucketTotal: number, currBucket: EventBucket) => {
        const currBucketTotalNotesSemiCorrect = currBucket.notes.reduce(
          (prevNoteTotal: number, currNote: NoteEvent) => {
            if (midiSemiErrorStates.includes(currNote.OffsetState)) {
              return prevNoteTotal + 1;
            }
            return prevNoteTotal;
          },
          0
        );

        const currBucketTotalRestsSemiCorrect = currBucket.rests.reduce(
          (prevRestsTotal: number, currRest: RestEvent) => {
            if (!currRest.Hidden) {
              return currRest.State !== MidiEventState.UNSET
                ? prevRestsTotal + 1
                : prevRestsTotal;
            } else {
              return prevRestsTotal;
            }
          },
          0
        );
        return (
          prevBucketTotal +
          currBucketTotalNotesSemiCorrect +
          currBucketTotalRestsSemiCorrect
        );
      },
      0
    );
    const totalCorrect = playData.reduce(
      (prevBucketTotal: number, currBucket: EventBucket) => {
        const totalCorrectNotes = currBucket.notes.reduce(
          (prevNoteTotal: number, currNote: NoteEvent) => {
            if (currNote.OffsetState == MidiEventState.HIT) {
              return prevNoteTotal + 1;
            }
            return prevNoteTotal;
          },
          0
        );
        const totalCorrectRests = currBucket.rests.reduce(
          (prevNoteTotal: number, currNote: RestEvent) => {
            if (currNote.State == MidiEventState.UNSET) {
              return prevNoteTotal + 1;
            }
            return prevNoteTotal;
          },
          0
        );
        return totalCorrectNotes + totalCorrectRests + prevBucketTotal;
      },
      0
    );
    logger.debug(`totalNotes = ${totalNotes}`);
    logger.debug(`totalRests = ${totalRests}`);
    logger.debug(`totalSemiCorrect = ${totalSemiCorrect}`);
    logger.debug(`totalCorrect = ${totalCorrect}`);
    logger.debug(
      `((.7 * totalOffTime) + (totalCorrect))/(totalNotes + totalRests) = ((.7 * ${totalSemiCorrect}) + (${totalCorrect}))/(${totalNotes} + ${totalRests}) - ${
        (0.7 * totalSemiCorrect + totalCorrect) / (totalNotes + totalRests)
      }`
    );
    if (totalNotes + totalRests === 0) {
      logger.debug('final result accuracyType: ', accuracyType, 100);
      return 100;
    } else {
      const accuracy =
        (0.7 * totalSemiCorrect + totalCorrect) / (totalNotes + totalRests);
      logger.debug('final result accuracyType: ', accuracyType, accuracy * 100);
      return accuracy * 100;
    }
  }

  private getAvgRunningAccuracy(
    accuracyType:
      | AccuracyTypes.avg_lh_running_accuracy
      | AccuracyTypes.avg_rh_running_accuracy
      | AccuracyTypes.avg_running_accuracy
  ): number | null {
    logger.info('running avg accuracy. playdata: ', this.playData);
    if (accuracyType === AccuracyTypes.avg_lh_running_accuracy) {
      logger.info(
        'getting avg lh running accuracy.',
        this.calcRunningAccuracy(HANDEDNESS.LEFT)
      );
      // logger.info("this.lhRunningAccuracy arr: ", this.lhRunningAccuracy)
      // logger.info("calculated lh running accuracy: ",  (sum(this.lhRunningAccuracy) / (this.lhRunningAccuracy.length * 100)) * 100)
      // return this.lhRunningAccuracy.length === 0 ? null :  (sum(this.lhRunningAccuracy) / (this.lhRunningAccuracy.length * 100)) * 100
      return this.calcRunningAccuracy(HANDEDNESS.LEFT);
    } else if (accuracyType === AccuracyTypes.avg_rh_running_accuracy) {
      logger.info(
        'getting avg rh running accuracy.',
        this.calcRunningAccuracy(HANDEDNESS.RIGHT)
      );
      // logger.info("this.rhRunningAccuracy arr: ", this.rhRunningAccuracy)
      // logger.info("calculated rh running accuracy: ", (sum(this.rhRunningAccuracy) / (this.rhRunningAccuracy.length * 100)) * 100)
      // return this.rhRunningAccuracy.length === 0 ? null : (sum(this.rhRunningAccuracy) / (this.rhRunningAccuracy.length * 100)) * 100
      return this.calcRunningAccuracy(HANDEDNESS.RIGHT);
    }
    logger.info('getting avg running accuracy.', this.calcRunningAccuracy());
    // logger.info("this.runningAccuracyy arr: ", this.runningAccuracy)
    // logger.info("calculated running accuracy: ", (sum(this.runningAccuracy) / (this.runningAccuracy.length * 100)) * 100)
    // return this.runningAccuracy.length === 0 ? null : (sum(this.runningAccuracy) / (this.runningAccuracy.length * 100)) * 100
    return this.calcRunningAccuracy();
  }

  private filterPlayDataByHand(hand: HANDEDNESS) {
    return this.playData.reduce(
      (listByhand: EventBucket[], currentNoteBucket: EventBucket) => {
        const evtBucketByHand = cloneDeep(currentNoteBucket);
        evtBucketByHand.notes = evtBucketByHand.notes.filter((note) => {
          return note.Hand === hand;
        });
        listByhand.push(evtBucketByHand);
        return listByhand;
      },
      []
    );
  }

  private filterPlayDataByAccuracyType(accuracyType: AccuracyType) {
    if (
      accuracyType === AccuracyTypes.avg_lh_accuracy ||
      accuracyType === AccuracyTypes.avg_lh_running_accuracy
    ) {
      return this.filterPlayDataByHand(HANDEDNESS.LEFT);
    } else if (
      accuracyType === AccuracyTypes.avg_rh_accuracy ||
      accuracyType === AccuracyTypes.avg_rh_running_accuracy
    ) {
      return this.filterPlayDataByHand(HANDEDNESS.RIGHT);
    }
    return this.playData;
  }

  private calculateErrorScore(
    errorData: PhraseNoteError[],
    hand?: HANDEDNESS
  ): number {
    return errorData.reduce((prevNoteErrAcc, noteError) => {
      if (hand && noteError.hand !== hand) {
        return prevNoteErrAcc;
      } else {
        if (midiAllErrorStates.includes(noteError.note_error_type)) {
          return (
            prevNoteErrAcc +
            errorScores[noteError.note_error_type as MidiFullErrorStates]
          );
        } else {
          return prevNoteErrAcc;
        }
      }
    }, 0);
  }

  private getErrorData(repertoire: boolean = false) {
    return this.playData.reduce(
      (prevErrors: PhraseNoteError[], noteBucket: EventBucket) => {
        const errors = [];
        for (let note of noteBucket.notes) {
          // In the case of repertoire, we want to preserve the tick location with respect to the rest of the piece.
          // In the sight-reading trainer, there is no overall "piece", so all timestamps are with respect to the start
          // of the phrase
          let playedTick =
            repertoire || note.PlayedTimestamp == null
              ? note.PlayedTimestamp
              : note.PlayedTimestamp - this.startTimestamp;
          let playedOffsetTick =
            repertoire || note.OffsetTimestamp == null
              ? note.PlayedTimestamp
              : note.OffsetTimestamp - this.startTimestamp;
          let tick =
            repertoire || noteBucket.timestamp == null
              ? noteBucket.timestamp
              : noteBucket.timestamp - this.startTimestamp;
          let offsetTick =
            repertoire || note.Timestamp == null
              ? note.Timestamp + note.Length
              : note.Timestamp + note.Length - this.startTimestamp;

          if (
            note.OffsetState === MidiEventState.NEVER_PLAYED ||
            note.OnsetState === MidiEventState.UNSET
          ) {
            playedTick = UNPLAYED_NOTE_TICK;
            playedOffsetTick = UNPLAYED_NOTE_TICK;
          }
          console.log('playedTick', playedTick);
          if (note.OffsetState !== MidiEventState.UNSET) {
            errors.push({
              note: note.MidiNote,
              note_error_type: note.OffsetState,
              tick: tick,
              played_tick: playedTick,
              offset_tick: offsetTick,
              played_offset_tick: playedOffsetTick,
              played_note:
                note.AdditionalNote == null
                  ? note.MidiNote
                  : note.AdditionalNote.PlayedNote,
              is_rest: false,
              hand: note.Hand,
              bpm: note?.SnapshotBPM,
              error_score: note.ErrorScore,
            });
          } else {
            // this is a stop gap in case somehow an event with an onset error gets through without that error being carried to offset
            errors.push({
              note: note.MidiNote,
              note_error_type: note.OnsetState,
              tick: tick,
              played_tick: playedTick,
              offset_tick: offsetTick,
              played_offset_tick: playedOffsetTick,
              played_note:
                note.AdditionalNote == null
                  ? note.MidiNote
                  : note.AdditionalNote.PlayedNote,
              is_rest: false,
              hand: note.Hand,
              bpm: note?.SnapshotBPM,
              error_score: note.ErrorScore,
            });
          }
        }
        // No need to include rests for now... additional notes are now their own value
        // for(let rest of noteBucket.rests) {
        //   // In the case of repertoire, we want to preserve the tick location with respect to the rest of the piece.
        //   // In the sight-reading trainer, there is no overall "piece", so all timestamps are with respect to the start
        //   // of the phrase
        //   let playedTick = repertoire ||  rest.PlayedTimestamp== null ? rest.PlayedTimestamp : rest.PlayedTimestamp -  this.startTimestamp;
        //   if(rest.OffsetState === MidiEventState.NEVER_PLAYED || rest.State === MidiEventState.UNSET) {
        //     playedTick = UNPLAYED_NOTE_TICK;
        //   }
        //   if(rest.AdditionalNote) {
        //     errors.push({
        //       note: -1,
        //       played_note: rest.AdditionalNote?.PlayedNote || null,
        //       note_error_type: rest.State,
        //       tick: noteBucket.timestamp,
        //       offset_tick: rest.Timestamp + rest.Length,
        //       played_tick: playedTick,
        //       played_offset_tick: rest.AdditionalNote.OffsetTimestamp,
        //       is_rest: true,
        //       hand: rest.Hand,
        //       bpm: rest.SnapshotBPM,
        //       error_score: rest.ErrorScore
        //     })
        //   }
        // }

        for (let additionalNote of noteBucket.additinalNotes) {
          // In the case of repertoire, we want to preserve the tick location with respect to the rest of the piece.
          // In the sight-reading trainer, there is no overall "piece", so all timestamps are with respect to the start
          // of the phrase
          let playedTick =
            repertoire || additionalNote.PlayedTimestamp == null
              ? additionalNote.PlayedTimestamp
              : additionalNote.PlayedTimestamp - this.startTimestamp;
          let playedOffsetTick =
            repertoire || additionalNote.OffsetTimestamp == null
              ? additionalNote.PlayedTimestamp
              : additionalNote.OffsetTimestamp - this.startTimestamp;
          let tick =
            repertoire || noteBucket.timestamp == null
              ? noteBucket.timestamp
              : noteBucket.timestamp - this.startTimestamp;
          let offsetTick =
            repertoire || additionalNote.Timestamp == null
              ? additionalNote.Timestamp + additionalNote.Length
              : additionalNote.Timestamp +
                additionalNote.Length -
                this.startTimestamp;

          if (
            additionalNote.OffsetState === MidiEventState.NEVER_PLAYED ||
            additionalNote.OnsetState === MidiEventState.UNSET
          ) {
            playedTick = UNPLAYED_NOTE_TICK;
            playedOffsetTick = UNPLAYED_NOTE_TICK;
          }

          errors.push({
            note_error_type: MidiEventState.ADDITIONAL_NOTE,
            tick: noteBucket.timestamp,
            played_tick: playedTick,
            offset_tick: null,
            played_offset_tick: additionalNote.OffsetTimestamp,
            note: -1,
            played_note: additionalNote?.PlayedNote || null,
            is_rest: true,
            hand: additionalNote.Hand,
            bpm: additionalNote.SnapshotBPM,
            error_score: additionalNote.ErrorScore,
          });
        }
        return prevErrors.concat(errors);
      },
      []
    );
  }

  // get all notes expected in phrase. Theoretically we should replace this with some tagging
  // from the music xml pipeline
  private getTotalExpectedNotes(hand?: HANDEDNESS) {
    return this.playData.reduce(
      (prevNotes: number, noteBucket: EventBucket) => {
        if (hand && noteBucket.notes.length > 0) {
          // filter additional note events because those are events we added to indicate noted that came in from
          // the user that were not part of the original expected note set
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) =>
                note.Hand === hand &&
                note.OnsetState !== MidiEventState.ADDITIONAL_NOTE
            ).length
          );
        } else {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) => note.OnsetState !== MidiEventState.ADDITIONAL_NOTE
            ).length
          );
        }
      },
      0
    );
  }

  // get all played notes.
  private getTotalPlayedNotes(hand?: HANDEDNESS) {
    return this.playData.reduce(
      (prevNotes: number, noteBucket: EventBucket) => {
        if (hand && noteBucket.notes.length > 0) {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) =>
                note.Hand === hand &&
                note.OffsetState !== MidiEventState.NEVER_PLAYED
            ).length +
            noteBucket.additinalNotes.filter((note) => note.Hand === hand)
              .length
          );
        } else {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) => note.OffsetState !== MidiEventState.NEVER_PLAYED
            ).length +
            noteBucket.additinalNotes.length
          );
        }
      },
      0
    );
  }

  public generatePhraseCompletedEventData() {
    const avgRunningAccuracy: number | null = this.getAvgRunningAccuracy(
      AccuracyTypes.avg_running_accuracy
    );
    const avgLHRunningAccuracy: number | null = this.getAvgRunningAccuracy(
      AccuracyTypes.avg_lh_running_accuracy
    );
    const avgRHRunningAccuracy: number | null = this.getAvgRunningAccuracy(
      AccuracyTypes.avg_rh_running_accuracy
    );
    const accuracy: number | null = this.getAccuracy(
      AccuracyTypes.avg_accuracy
    );
    const rhAccuracy: number | null = this.getAccuracy(
      AccuracyTypes.avg_rh_accuracy
    );
    const lhAccuracy: number | null = this.getAccuracy(
      AccuracyTypes.avg_lh_accuracy
    );
    const errorData: PhraseNoteError[] = this.getErrorData();
    const totalNotes: number = this.getTotalPlayedNotes();
    const lhTotalNotes: number = this.getTotalPlayedNotes(HANDEDNESS.LEFT);
    const rhTotalNotes: number = this.getTotalPlayedNotes(HANDEDNESS.RIGHT);
    const lhErrorScore: number = this.calculateErrorScore(
      errorData,
      HANDEDNESS.LEFT
    );
    const rhErrorScore: number = this.calculateErrorScore(
      errorData,
      HANDEDNESS.RIGHT
    );
    const errorScore: number = this.calculateErrorScore(errorData);
    logger.debug('error data in phrase');
    logger.debug(errorData);
    const midiData = this.midiEvents;
    return {
      // need to fix these but
      avgRunningAccuracy: 0,
      avgLHRunningAccuracy: 0,
      avgRHRunningAccuracy: 0,
      accuracy,
      rhAccuracy,
      lhAccuracy,
      errorData,
      midiData,
      totalNotes,
      lhTotalNotes,
      rhTotalNotes,
      errorScore,
      lhErrorScore,
      rhErrorScore,
    };
  }

  private getAvgBpm() {
    const totalNotes = this.playData.length;
    const totalBPM = this.playData.reduce(
      (prevBPM, noteBucket: EventBucket) => {
        if (noteBucket.notes.length > 0) {
          return prevBPM + noteBucket.notes[0].SnapshotBPM || 0;
        }
        return prevBPM;
      },
      0
    );
    if (totalNotes > 0) {
      return totalBPM / totalNotes;
    } else {
      return 0;
    }
  }

  private getAccuracyV2(
    accuracyType:
      | AccuracyTypes.avg_lh_accuracy
      | AccuracyTypes.avg_rh_accuracy
      | AccuracyTypes.avg_accuracy
  ): number {
    logger.info('this.numNotes', this.numNotes);
    if (accuracyType === AccuracyTypes.avg_lh_accuracy) {
      const totalNotes = this.playData.reduce(
        (prevNotes: number, noteBucket: EventBucket) => {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) =>
                note.Hand === HANDEDNESS.LEFT &&
                note.OnsetState !== MidiEventState.ADDITIONAL_NOTE
            ).length
          );
        },
        0
      );
      const lHErrorScore = this.playData.reduce(
        (acc: number, datum: EventBucket) => {
          const noteErrorSum = datum.notes.reduce(
            (acc: number, note: NoteEvent) => {
              return note.Hand === HANDEDNESS.LEFT
                ? note.ErrorScore + acc
                : acc;
            },
            0
          );

          const restErrorSum = datum.rests.reduce(
            (acc: number, rest: RestEvent) => {
              return rest.Hand === HANDEDNESS.LEFT
                ? rest.ErrorScore + acc
                : acc;
            },
            0
          );

          return noteErrorSum + restErrorSum + acc;
        },
        0
      );
      return (totalNotes - lHErrorScore) / totalNotes;
    } else if (accuracyType === AccuracyTypes.avg_rh_accuracy) {
      const totalNotes = this.playData.reduce(
        (prevNotes: number, noteBucket: EventBucket) => {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) =>
                note.Hand === HANDEDNESS.RIGHT &&
                note.OnsetState !== MidiEventState.ADDITIONAL_NOTE
            ).length
          );
        },
        0
      );
      const rHErrorScore = this.playData.reduce(
        (acc: number, datum: EventBucket) => {
          const noteErrorSum = datum.notes.reduce(
            (acc: number, note: NoteEvent) => {
              return note.Hand === HANDEDNESS.RIGHT
                ? note.ErrorScore + acc
                : acc;
            },
            0
          );

          const restErrorSum = datum.rests.reduce(
            (acc: number, rest: RestEvent) => {
              return rest.Hand === HANDEDNESS.RIGHT
                ? rest.ErrorScore + acc
                : acc;
            },
            0
          );

          return noteErrorSum + restErrorSum + acc;
        },
        0
      );
      return (totalNotes - rHErrorScore) / totalNotes;
    } else {
      const totalNotes = this.playData.reduce(
        (prevNotes: number, noteBucket: EventBucket) => {
          return (
            prevNotes +
            noteBucket.notes.filter(
              (note) => note.OnsetState !== MidiEventState.ADDITIONAL_NOTE
            ).length
          );
        },
        0
      );
      const errorScore = this.playData.reduce(
        (acc: number, datum: EventBucket) => {
          const noteErrorSum = datum.notes.reduce(
            (acc: number, note: NoteEvent) => {
              return note.ErrorScore + acc;
            },
            0
          );

          const restErrorSum = datum.rests.reduce(
            (acc: number, rest: RestEvent) => {
              return rest.ErrorScore + acc;
            },
            0
          );

          return noteErrorSum + restErrorSum + acc;
        },
        0
      );
      return (totalNotes - errorScore) / totalNotes;
    }
  }

  public generateRepPhraseCompletedEventData() {
    const accuracy: number | null = this.getAccuracyV2(
      AccuracyTypes.avg_accuracy
    );
    const rhAccuracy: number = this.getAccuracyV2(
      AccuracyTypes.avg_rh_accuracy
    );
    const lhAccuracy: number = this.getAccuracyV2(
      AccuracyTypes.avg_lh_accuracy
    );
    const errorData: PhraseNoteError[] = this.getErrorData(true);
    const totalNotes: number = this.getTotalPlayedNotes();
    const lhTotalNotes: number = this.getTotalPlayedNotes(HANDEDNESS.LEFT);
    const rhTotalNotes: number = this.getTotalPlayedNotes(HANDEDNESS.RIGHT);
    const lhErrorScore: number = this.calculateErrorScore(
      errorData,
      HANDEDNESS.LEFT
    );
    const rhErrorScore: number = this.calculateErrorScore(
      errorData,
      HANDEDNESS.RIGHT
    );
    const errorScore: number = this.calculateErrorScore(errorData);
    const bpm = this.getAvgBpm();
    const midiData = this.midiEvents;
    return {
      accuracy,
      rhAccuracy,
      lhAccuracy,
      errorData,
      midiData,
      totalNotes,
      lhTotalNotes,
      rhTotalNotes,
      errorScore,
      lhErrorScore,
      rhErrorScore,
      bpm,
    };
  }
}

export default Phrase;
